import React, { useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useWorkspaceBranding } from 'src/utils/workspace-logo'
import { loadFont } from 'src/pages/workspace/branding/font-select'
import { DEFAULT_LOGO_SIZE, mediaUrl, textScale } from './brand-fields'
import useStyles from './styles'

// "#RRGGBB" with an alpha byte, for tints of the text color.
const withAlpha = (color, alpha) =>
  `${color.slice(0, 7)}${Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0')}`

/**
 * Header colors from its text color, as the learner portal draws them: the
 * profile button is the text color with the header color on it; the bottom
 * line and logo tile border are light tints of the text color.
 */
export const headerTheme = (background, text) => ({
  fg: text,
  line: withAlpha(text, 0.12),
  tileBorder: withAlpha(text, 0.15),
  avatarBg: text,
  avatarFg: background,
})

/**
 * Menu rows: the saved menu items, then catalog / custom pages that are not in
 * the menu yet (shown hidden, so the toggle can add them).
 */
export const menuRows = portal => {
  const pages = portal.pages || []
  const listed = new Set(
    (portal.menuItems || [])
      .filter(item => item.type === 'page')
      .map(item => item.pageId),
  )
  const rows = (portal.menuItems || []).filter(
    item => item.type === 'link' || pages.some(page => page.id === item.pageId),
  )
  pages
    .filter(
      page =>
        (page.type === 'catalog' || page.type === 'custom') &&
        !listed.has(page.id),
    )
    .forEach(page =>
      rows.push({ type: 'page', pageId: page.id, visible: false }),
    )
  return rows
}

export const SOCIAL_NETWORKS = [
  ['linkedin', 'LinkedIn', 'tabler:brand-linkedin', 'linkedin.com/company/'],
  ['youtube', 'YouTube', 'tabler:brand-youtube', 'youtube.com/@'],
  ['x', 'X', 'tabler:brand-x', 'x.com/'],
  ['notion', 'Notion', 'tabler:brand-notion', 'notion.so/'],
  ['discord', 'Discord', 'tabler:brand-discord', 'discord.gg/'],
  ['whatsapp', 'WhatsApp', 'tabler:brand-whatsapp', 'wa.me/'],
  ['telegram', 'Telegram', 'tabler:brand-telegram', 't.me/'],
  ['facebook', 'Facebook', 'tabler:brand-facebook', 'facebook.com/'],
  ['instagram', 'Instagram', 'tabler:brand-instagram', 'instagram.com/'],
  ['pinterest', 'Pinterest', 'tabler:brand-pinterest', 'pinterest.com/'],
].map(([key, label, icon, placeholder]) => ({ key, label, icon, placeholder }))

export const networkOf = key =>
  SOCIAL_NETWORKS.find(network => network.key === key)

export const defaultCopyright = shopName =>
  `© ${new Date().getFullYear()}${
    shopName ? ` ${shopName}` : ''
  }. All rights reserved.`

/**
 * The portal header as learners see it: the workspace logo (or tile and
 * name), the visible menu items and the profile button.
 * `logoSize` overrides the saved size while its slider is dragged.
 */
/**
 * The workspace font (Workspace › Branding) as a font-family, loaded for the
 * preview: the learner portal's header and footer use it (Inter when unset).
 */
const usePortalFont = () => {
  const font = useWorkspaceBranding()?.font || 'Inter'
  useEffect(() => {
    loadFont(font)
  }, [font])
  return `'${font.replace(/['"]/g, '')}', 'Inter', system-ui, sans-serif`
}

export const PortalHeaderBar = ({
  portal,
  logo,
  shopName,
  logoSize,
  signInLabel = 'Sign in',
}) => {
  const classes = useStyles()
  const fontFamily = usePortalFont()
  const pages = portal.pages || []
  const background = portal.headerBackgroundColor || '#FFFFFF'
  const theme = headerTheme(background, portal.headerTextColor || '#000000')
  const size = logoSize || portal.headerLogoSize || DEFAULT_LOGO_SIZE.header
  const scale = textScale('header', portal.headerTextSize)
  const menu = menuRows(portal)
    .filter(row => row.visible)
    .map(row =>
      row.type === 'link'
        ? row.label
        : pages.find(page => page.id === row.pageId)?.name,
    )
    .filter(Boolean)

  return (
    <div
      className={classes.headerBar}
      style={{
        background,
        borderBottom: `1px solid ${theme.line}`,
        fontFamily,
      }}
    >
      <div className={classes.brand} style={{ color: theme.fg }}>
        {logo ? (
          <img
            src={mediaUrl(logo)}
            alt=""
            className={classes.previewLogo}
            style={{ height: size }}
          />
        ) : (
          // Without a logo, the workspace name alone (no placeholder mark).
          <span className={classes.brandName} style={{ fontSize: scale.name }}>
            {shopName}
          </span>
        )}
      </div>
      <div
        className={classes.headerMenu}
        style={{ color: theme.fg, fontSize: scale.link }}
      >
        {menu.map(label => (
          <span key={label}>{label}</span>
        ))}
        {/* As a visitor sees it: "Sign in"; signed-in learners get the
            profile icon instead. */}
        {portal.showSignIn && (
          <span
            className={classes.signInButton}
            style={{ background: theme.avatarBg, color: theme.avatarFg }}
            title="Signed-in learners see a profile icon here"
          >
            {signInLabel}
          </span>
        )}
      </div>
    </div>
  )
}

const hexToRgb = hex => {
  let value = String(hex || '').replace('#', '')
  if (value.length === 3) value = value.replace(/./g, ch => ch + ch)
  const n = parseInt(value.slice(0, 6), 16)
  return Number.isNaN(n)
    ? [255, 255, 255]
    : [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const rgb = c => `rgb(${c.map(Math.round).join(', ')})`

/** `a` with `share` (0–1) of `b` mixed in. */
const mix = (a, b, share) => a.map((value, i) => value * (1 - share) + b[i] * share)

/**
 * Breadcrumb strip colors from the header's, as in the learner app
 * (components/portal-breadcrumbs): a shade darker than the header (a touch
 * on a light one, clearly on a dark one), links in the header text color, the
 * current page and separators faded toward the strip.
 */
export const crumbTheme = (background, text) => {
  const nav = hexToRgb(background)
  const dark = (0.299 * nav[0] + 0.587 * nav[1] + 0.114 * nav[2]) / 255 < 0.5
  const bg = mix(nav, [0, 0, 0], dark ? 0.22 : 0.035)
  const fg = hexToRgb(text)
  return {
    background: rgb(bg),
    border: dark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.05)',
    link: text,
    current: rgb(mix(fg, bg, 0.4)),
    separator: rgb(mix(fg, bg, 0.6)),
  }
}

/**
 * The breadcrumb learners see under the portal header on path and course
 * pages (Portal › Navigation › Show breadcrumbs): `items` are the labels,
 * Home first; the last one is the page on screen.
 */
export const PortalBreadcrumbBar = ({ portal, items }) => {
  const fontFamily = usePortalFont()
  const theme = crumbTheme(
    portal.headerBackgroundColor || '#FFFFFF',
    portal.headerTextColor || '#000000',
  )
  const scale = textScale('header', portal.headerTextSize)

  return (
    <nav
      aria-label="Breadcrumb"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        height: 48,
        // Same left edge as the header's logo.
        padding: '0 28px',
        background: theme.background,
        borderBottom: `1px solid ${theme.border}`,
        fontFamily,
        fontSize: scale.link,
        whiteSpace: 'nowrap',
        minWidth: 0,
      }}
    >
      {items.map((label, index) => {
        const last = index === items.length - 1
        return (
          <React.Fragment key={`${index}-${label}`}>
            {index > 0 && (
              <span aria-hidden="true" style={{ color: theme.separator }}>
                ›
              </span>
            )}
            <span
              aria-current={last ? 'page' : undefined}
              style={{
                color: last ? theme.current : theme.link,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {label}
            </span>
          </React.Fragment>
        )
      })}
    </nav>
  )
}

/**
 * The portal footer as learners see it: logo (or name), links, social icons
 * and the copyright line. `logoSize` overrides the saved size while dragged.
 */
export const PortalFooterBar = ({ portal, logo, shopName, logoSize }) => {
  const classes = useStyles()
  const fontFamily = usePortalFont()
  const pages = portal.pages || []
  // Links to a page that was removed are left out.
  const links = (portal.footerLinks || []).filter(
    link => !link.pageId || pages.some(page => page.id === link.pageId),
  )
  const socials = (portal.socialLinks || []).filter(s => networkOf(s.network))
  const size = logoSize || portal.footerLogoSize || DEFAULT_LOGO_SIZE.footer
  const scale = textScale('footer', portal.footerTextSize)
  const copyright = portal.copyright ?? defaultCopyright(shopName)

  return (
    <div
      className={classes.footerBody}
      style={{
        background: portal.footerBackgroundColor || '#FFFFFF',
        color: portal.footerTextColor || '#000000',
        fontFamily,
      }}
    >
      <div className={classes.footerTop}>
        <div className={classes.brand}>
          {logo ? (
            <img
              src={mediaUrl(logo)}
              alt=""
              className={classes.previewLogo}
              style={{ height: size }}
            />
          ) : (
            // Without a logo, the workspace name alone (no placeholder mark).
            <span style={{ fontSize: scale.name, fontWeight: 600 }}>
              {shopName}
            </span>
          )}
        </div>
        <div className={classes.footerLinks} style={{ fontSize: scale.link }}>
          {links.map(link => (
            <span key={link.id}>{link.label}</span>
          ))}
        </div>
        {socials.length > 0 && (
          <div className={classes.footerSocials}>
            {socials.map(social => (
              <Icon
                key={social.network}
                icon={networkOf(social.network).icon}
                width={16}
                height={16}
              />
            ))}
          </div>
        )}
      </div>
      {copyright.trim() && (
        <div className={classes.footerCopy}>{copyright}</div>
      )}
    </div>
  )
}
